import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { ProtectedRoute } from "@/components/vendor/ProtectedRoute";
import VendorLayout from "@/components/vendor/VendorLayout";
import { AdminProtectedRoute } from "@/components/admin/AdminProtectedRoute";
import AdminLayout from "@/components/admin/AdminLayout";

import MarketingPage from "@/pages/MarketingPage";
import PlayPage from "@/pages/public/PlayPage";
import CouponPage from "@/pages/public/CouponPage";
import OfferPage from "@/pages/public/OfferPage";
import ExplorePage from "@/pages/public/ExplorePage";
import ForBusinessPage from "@/pages/public/ForBusinessPage";
import CustomerLoginPage from "@/pages/customer/CustomerLoginPage";
import MyWinningsPage from "@/pages/customer/MyWinningsPage";

// Vendor
import LoginPage from "@/pages/vendor/LoginPage";
import RegisterPage from "@/pages/vendor/RegisterPage";
import ForgotPasswordPage from "@/pages/vendor/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/vendor/ResetPasswordPage";
import OverviewPage from "@/pages/vendor/OverviewPage";
import CampaignsPage from "@/pages/vendor/CampaignsPage";
import CampaignWizardPage from "@/pages/vendor/CampaignWizardPage";
import CampaignAnalyticsPage from "@/pages/vendor/CampaignAnalyticsPage";
import LeadsPage from "@/pages/vendor/LeadsPage";
import VerifyPage from "@/pages/vendor/VerifyPage";
import RetargetingPage from "@/pages/vendor/RetargetingPage";
import BillingPage from "@/pages/vendor/BillingPage";
import WalletPage from "@/pages/vendor/WalletPage";
import WalletLedgerPage from "@/pages/vendor/WalletLedgerPage";
import CheckoutPage from "@/pages/vendor/CheckoutPage";
import SettingsPage from "@/pages/vendor/SettingsPage";

// Admin
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminVendorsPage from "@/pages/admin/AdminVendorsPage";
import AdminVendorDetailPage from "@/pages/admin/AdminVendorDetailPage";
import AdminCampaignsPage from "@/pages/admin/AdminCampaignsPage";
import AdminPopularStoresPage from "@/pages/admin/AdminPopularStoresPage";
import AdminSupportPage from "@/pages/admin/AdminSupportPage";
import AdminRetargetingPage from "@/pages/admin/AdminRetargetingPage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";
import AdminCustomersPage from "@/pages/admin/AdminCustomersPage";
import AdminInvoicesPage from "@/pages/admin/AdminInvoicesPage";
import AdminIntakeLeadsPage from "@/pages/admin/AdminIntakeLeadsPage";
import SchedulerControlsPage from "@/pages/admin/SchedulerControlsPage";
import AdminEnquiriesPage from "@/pages/admin/AdminEnquiriesPage";
import AdminEnquiryDetailPage from "@/pages/admin/AdminEnquiryDetailPage";
import SuperAdminReferralCodesPage from "@/pages/admin/SuperAdminReferralCodesPage";
import SuperAdminCitiesPage from "@/pages/admin/SuperAdminCitiesPage";
import SuperAdminCityIntegrityPage from "@/pages/admin/SuperAdminCityIntegrityPage";
import AdminCreateCampaignPage from "@/pages/admin/AdminCreateCampaignPage";
import SuperAdminBillingCouponsPage from "@/pages/admin/SuperAdminBillingCouponsPage";
import SuperAdminFranchisesPage from "@/pages/admin/SuperAdminFranchisesPage";
import SuperAdminBannersPage from "@/pages/admin/SuperAdminBannersPage";
import AdminSetPasswordPage from "@/pages/admin/AdminSetPasswordPage";
import FranchiseOverviewPage from "@/pages/franchise/FranchiseOverviewPage";
import FranchiseStaffPage from "@/pages/franchise/FranchiseStaffPage";
import FranchiseHomepageEditorPage from "@/pages/franchise/FranchiseHomepageEditorPage";
import FranchiseActivityPage from "@/pages/franchise/FranchiseActivityPage";
import FranchiseBillingCouponsPage from "@/pages/franchise/FranchiseBillingCouponsPage";
import FranchiseRevenuePage from "@/pages/franchise/FranchiseRevenuePage";

// Public
import GuidedSetupPage from "@/pages/public/GuidedSetupPage";
import ContactPage from "@/pages/public/ContactPage";

// Super Admin
import SuperAdminLoginPage from "@/pages/admin/SuperAdminLoginPage";
import SuperAdminDashboardPage from "@/pages/admin/SuperAdminDashboardPage";
import SuperAdminAdminsPage from "@/pages/admin/SuperAdminAdminsPage";
import SuperAdminPricingPage from "@/pages/admin/SuperAdminPricingPage";
import SuperAdminAnalyticsPage from "@/pages/admin/SuperAdminAnalyticsPage";

// Coupon Verifier — standalone tool for on-site event staff.
// Not linked from any vendor or admin nav; accessed directly at /verify.
import VerifierPage from "@/pages/verifier/VerifierPage";

// Alias forwarders so legacy URLs still work.
const SpinAlias = () => {
  const { slug } = useParams();
  return <Navigate to={`/play/${slug}`} replace />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AdminAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<MarketingPage />} />
              <Route path="/for-business" element={<ForBusinessPage />} />

              {/* Public game */}
              <Route path="/play/:campaignId" element={<PlayPage />} />
              <Route path="/spin/:slug" element={<SpinAlias />} />
              <Route path="/coupon/:code" element={<CouponPage />} />
              {/* Static-offer landing page — no game, instant claim. */}
              <Route path="/offers/:id" element={<OfferPage />} />
              {/* Explore / Near-You feed of all publicly-listed
                  campaigns across game types. */}
              <Route path="/explore" element={<ExplorePage />} />

              {/* Customer account (email-OTP) — separate role from
                  vendor / admin / verifier. My Winnings surfaces every
                  spin/scratch/offer they've claimed while signed in. */}
              <Route path="/customer/login" element={<CustomerLoginPage />} />
              <Route path="/customer/my-winnings" element={<MyWinningsPage />} />
              <Route path="/customer/profile" element={<MyWinningsPage />} />

              {/* Coupon Verifier — standalone, login-gated but with
                  its own auth stack. Not linked from vendor/admin nav.
                  Superadmins provision verifier accounts under
                  /superadmin/admins → Verifiers tab. */}
              <Route path="/verify" element={<VerifierPage />} />

              {/* Vendor auth */}
              <Route path="/vendor/login" element={<LoginPage />} />
              <Route path="/vendor/register" element={<RegisterPage />} />
              {/* Guided Setup — public onboarding questionnaire that ends
                  in the same vendor-registration flow, just with intake
                  answers stored alongside the vendor doc. */}
              <Route path="/guided-setup" element={<GuidedSetupPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/vendor/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/vendor/reset-password" element={<ResetPasswordPage />} />
              <Route path="/login" element={<Navigate to="/vendor/login" replace />} />
              <Route path="/signup" element={<Navigate to="/vendor/register" replace />} />

              {/* Vendor app */}
              <Route
                path="/vendor"
                element={<ProtectedRoute><VendorLayout /></ProtectedRoute>}
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<OverviewPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="campaigns/new" element={<CampaignWizardPage />} />
                <Route path="campaigns/:id/analytics" element={<CampaignAnalyticsPage />} />
                <Route path="campaigns/:id" element={<CampaignWizardPage />} />
                <Route path="checkout/:campaignId" element={<CheckoutPage />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="verify" element={<VerifyPage />} />
                <Route path="retargeting" element={<RetargetingPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="wallet" element={<WalletPage />} />
                <Route path="wallet/ledger" element={<WalletLedgerPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
              <Route path="/dashboard" element={<Navigate to="/vendor/dashboard" replace />} />
              <Route path="/dashboard/*" element={<Navigate to="/vendor/dashboard" replace />} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin/set-password" element={<AdminSetPasswordPage />} />
              <Route
                path="/admin"
                element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboardPage />} />
                <Route path="vendors" element={<AdminVendorsPage />} />
                <Route path="vendors/:id" element={<AdminVendorDetailPage />} />
                <Route path="campaigns" element={<AdminCampaignsPage />} />
                <Route path="campaigns/new" element={<AdminCreateCampaignPage />} />
                <Route path="popular-stores" element={<AdminPopularStoresPage />} />
                <Route path="customers" element={<AdminCustomersPage />} />
                <Route path="invoices" element={<AdminInvoicesPage />} />
                <Route path="support" element={<AdminSupportPage />} />
                <Route path="retargeting" element={<AdminRetargetingPage />} />
                <Route path="reports" element={<AdminReportsPage />} />
                <Route path="intake-leads" element={<AdminIntakeLeadsPage />} />
                <Route path="scheduler" element={<SchedulerControlsPage />} />
                <Route path="enquiries" element={<AdminEnquiriesPage />} />
                <Route path="enquiries/:id" element={<AdminEnquiryDetailPage />} />
              </Route>

              {/* Super Admin */}
              <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
              <Route
                path="/superadmin"
                element={<AdminProtectedRoute requireSuperAdmin><AdminLayout superadmin /></AdminProtectedRoute>}
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<SuperAdminDashboardPage />} />
                <Route path="vendors" element={<AdminVendorsPage superadmin />} />
                <Route path="vendors/:id" element={<AdminVendorDetailPage superadmin />} />
                <Route path="campaigns" element={<AdminCampaignsPage />} />
                <Route path="popular-stores" element={<AdminPopularStoresPage />} />
                <Route path="customers" element={<AdminCustomersPage superadmin />} />
                <Route path="invoices" element={<AdminInvoicesPage />} />
                <Route path="admins" element={<SuperAdminAdminsPage />} />
                <Route path="pricing" element={<SuperAdminPricingPage />} />
                <Route path="analytics" element={<SuperAdminAnalyticsPage />} />
                <Route path="intake-leads" element={<AdminIntakeLeadsPage superadmin />} />
                <Route path="scheduler" element={<SchedulerControlsPage />} />
                <Route path="enquiries" element={<AdminEnquiriesPage superadmin />} />
                <Route path="enquiries/:id" element={<AdminEnquiryDetailPage superadmin />} />
                <Route path="referral-codes" element={<SuperAdminReferralCodesPage />} />
                <Route path="billing-coupons" element={<SuperAdminBillingCouponsPage />} />
                <Route path="cities" element={<SuperAdminCitiesPage />} />
                <Route path="city-integrity" element={<SuperAdminCityIntegrityPage />} />
                <Route path="franchises" element={<SuperAdminFranchisesPage />} />
                <Route path="banners" element={<SuperAdminBannersPage />} />
              </Route>

              {/* Franchise dashboard — reuses AdminLayout with the
                  franchise flag; AdminProtectedRoute accepts
                  franchise + franchise_staff roles when the
                  `requireFranchise` prop is set. */}
              <Route
                path="/franchise"
                element={<AdminProtectedRoute requireFranchise><AdminLayout franchise /></AdminProtectedRoute>}
              >
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<FranchiseOverviewPage />} />
                <Route path="staff" element={<FranchiseStaffPage />} />
                <Route path="homepage-editor" element={<FranchiseHomepageEditorPage />} />
                <Route path="activity" element={<FranchiseActivityPage />} />
                <Route path="vendors" element={<AdminVendorsPage />} />
                <Route path="campaigns" element={<AdminCampaignsPage />} />
                <Route path="enquiries" element={<AdminEnquiriesPage />} />
                <Route path="leads" element={<AdminIntakeLeadsPage />} />
                <Route path="billing-coupons" element={<FranchiseBillingCouponsPage />} />
                <Route path="revenue" element={<FranchiseRevenuePage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AdminAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
